'use client';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { Search, MapPin, BedDouble, Bath, Maximize2, Heart, ChevronDown, Map, List, RefreshCw, Navigation, Grid3X3, CheckCircle, Phone, MessageCircle, Filter, X, Star, SlidersHorizontal } from 'lucide-react';
import { getActiveListings, mapToListingCard } from '@/lib/services/listingsService';
import { getSubcategories, getCategoryBySlug, CategoryRow } from '@/lib/services/categoriesService';

type GoogleMaps = typeof globalThis extends { google: infer G } ? G : {
  maps: {
    Map: new (el: HTMLElement, opts: object) => {
      setCenter: (c: { lat: number; lng: number }) => void;
      setZoom: (z: number) => void;
      fitBounds: (b: object, p?: object) => void;
    };
    Marker: new (opts: object) => {
      setMap: (map: object | null) => void;
      addListener: (event: string, cb: () => void) => void;
    };
    LatLngBounds: new () => {
      extend: (p: { lat: number; lng: number }) => void;
    };
    Size: new (w: number, h: number) => object;
    marker?: {
      AdvancedMarkerElement: new (opts: object) => {
        setMap: (map: object | null) => void;
        addListener: (event: string, cb: () => void) => void;
      };
    };
  };
};

declare global {
  interface Window {
    google: GoogleMaps;
  }
}

interface Listing {
  id: string;
  title: string;
  price: string;
  priceRaw: number;
  pricePerSqft?: string;
  location: string;
  area: string;
  beds: number;
  baths: number;
  type: string;
  status: 'For Sale' | 'For Rent';
  badge?: string;
  image: string;
  imageAlt: string;
  seller: string;
  sellerType: string;
  date: string;
  isVerified: boolean;
  isFeatured: boolean;
  lat?: number;
  lng?: number;
}

const sortOptions = [
  { id: 'newest', label: 'Newest First' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
  { id: 'popular', label: 'Featured First' },
];

const AMENITIES_OPTIONS = [
  'Swimming Pool', 'Gym', 'Parking', 'Security', 'Lift', 'Power Backup',
  'Garden', 'Club House', 'Children Play Area', 'CCTV', 'Intercom', 'Gated Community',
];

const APPROVAL_STATUS_OPTIONS = ['All', 'Approved', 'Pending', 'Rejected'];
const SELLER_RATING_OPTIONS = [
  { label: 'Any Rating', value: 0 },
  { label: '4★ & above', value: 4 },
  { label: '3★ & above', value: 3 },
  { label: '2★ & above', value: 2 },
];

// ─── Google Maps Component ────────────────────────────────────────────────────
function GoogleMapView({ listings, activeId, onMarkerClick }: { listings: Listing[]; activeId: string | null; onMarkerClick: (id: string) => void }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Window['google']['maps']['Map'] | null>(null);
  const markersRef = useRef<Map<string, ReturnType<Window['google']['maps']['Marker']['prototype']['constructor']>>>(new Map());
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);

  // Fetch Google Maps API key from platform_settings via API route
  useEffect(() => {
    fetch('/api/settings/maps-key')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.apiKey) {
          setApiKey(data.apiKey);
        } else {
          setMapError(true);
        }
      })
      .catch(() => setMapError(true));
  }, []);

  useEffect(() => {
    if (!apiKey || !mapRef.current) {
      return;
    }

    const scriptId = 'google-maps-script';
    if (document.getElementById(scriptId)) {
      if (window.google?.maps) {
        initMap();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker`;
    script.async = true;
    script.defer = true;
    script.onload = () => initMap();
    script.onerror = () => setMapError(true);
    document.head.appendChild(script);

    return () => {
      // cleanup markers on unmount
      markersRef.current.forEach(m => {
        if ('setMap' in m) m.setMap(null);
      });
      markersRef.current.clear();
    };
  }, [apiKey]);

  function initMap() {
    if (!mapRef.current) return;
    const center = { lat: 12.9716, lng: 77.5946 }; // Bengaluru
    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 11,
      mapId: 'nammaprice_map',
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    });
    mapInstanceRef.current = map;
    setMapLoaded(true);
  }

  // Update markers when listings change
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;
    const withCoords = listings.filter(l => l.lat && l.lng);

    // Remove old markers
    markersRef.current.forEach(m => {
      if ('setMap' in m) m.setMap(null);
    });
    markersRef.current.clear();

    // Add new markers
    withCoords.forEach(listing => {
      const isActive = activeId === listing.id;
      const markerEl = document.createElement('div');
      markerEl.className = `map-price-marker ${isActive ? 'map-price-marker-active' : ''}`;
      markerEl.textContent = listing.price;
      markerEl.style.cssText = `
        background: ${isActive ? '#D93025' : 'white'};
        color: ${isActive ? 'white' : '#1D1D1F'};
        border: 2px solid ${isActive ? '#D93025' : '#1D1D1F'};
        border-radius: 20px;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        transition: all 0.15s;
      `;

      let marker: ReturnType<Window['google']['maps']['Marker']['prototype']['constructor']>;
      try {
        marker = new window.google.maps.Marker({
          position: { lat: listing.lat!, lng: listing.lng! },
          map,
          icon: { url: 'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>', scaledSize: new window.google.maps.Size(1, 1) },
          label: {
            text: listing.price,
            color: isActive ? '#FFFFFF' : '#1D1D1F',
            fontWeight: '700',
            fontSize: '11px',
          },
          title: listing.title,
        });
        marker.addListener('click', () => onMarkerClick(listing.id));
        markersRef.current.set(listing.id, marker);
      } catch {
        // fallback simple marker
        const simpleMarker = new window.google.maps.Marker({
          position: { lat: listing.lat!, lng: listing.lng! },
          map,
          title: listing.title,
        });
        simpleMarker.addListener('click', () => onMarkerClick(listing.id));
        markersRef.current.set(listing.id, simpleMarker);
      }
    });

    // Fit bounds if we have markers
    if (withCoords.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      withCoords.forEach(l => bounds.extend({ lat: l.lat!, lng: l.lng! }));
      if (withCoords.length === 1) {
        map.setCenter({ lat: withCoords[0].lat!, lng: withCoords[0].lng! });
        map.setZoom(14);
      } else {
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      }
    }
  }, [listings, mapLoaded, activeId, onMarkerClick]);

  if (mapError || !apiKey) {
    return <StaticMapFallback listings={listings} activeId={activeId} onMarkerClick={onMarkerClick} />;
  }

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full" />
      {!mapLoaded && (
        <div className="absolute inset-0 bg-[#E8EDF2] flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#D93025] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-[12px] text-[#6E6E73]">Loading map...</p>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 right-2 text-[9px] text-[#A1A1A6] bg-white/80 px-1 rounded">Map data © Google</div>
    </div>
  );
}

// ─── Static Map Fallback (when no API key) ───────────────────────────────────
function StaticMapFallback({ listings, activeId, onMarkerClick }: { listings: Listing[]; activeId: string | null; onMarkerClick: (id: string) => void }) {
  const withCoords = listings.filter((l) => l.lat && l.lng);
  return (
    <div className="relative w-full h-full bg-[#E8EDF2] overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: `linear-gradient(rgba(180,195,210,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(180,195,210,0.35) 1px, transparent 1px)`,
        backgroundSize: '48px 48px'
      }} />
      <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
        <path d="M0 300 Q200 280 400 300 Q600 320 800 300" stroke="#94A3B8" strokeWidth="10" fill="none" />
        <path d="M400 0 Q380 150 400 300 Q420 450 400 600" stroke="#94A3B8" strokeWidth="10" fill="none" />
        <path d="M0 150 Q300 130 600 160 Q700 165 800 150" stroke="#CBD5E1" strokeWidth="5" fill="none" />
        <path d="M0 450 Q200 440 500 460 Q650 465 800 450" stroke="#CBD5E1" strokeWidth="5" fill="none" />
        <ellipse cx="150" cy="200" rx="70" ry="45" fill="#86EFAC" opacity="0.35" />
        <ellipse cx="650" cy="400" rx="55" ry="38" fill="#86EFAC" opacity="0.35" />
        <ellipse cx="500" cy="150" rx="45" ry="28" fill="#93C5FD" opacity="0.4" />
        <rect x="200" y="250" width="80" height="60" rx="4" fill="#E2E8F0" opacity="0.5" />
        <rect x="550" y="300" width="60" height="50" rx="4" fill="#E2E8F0" opacity="0.5" />
      </svg>
      {withCoords.map((listing) => {
        const x = (listing.lng! - 77.55) / 0.25 * 100;
        const y = (13.06 - listing.lat!) / 0.25 * 100;
        const isActive = activeId === listing.id;
        return (
          <button
            key={listing.id}
            onClick={() => onMarkerClick(listing.id)}
            className={`absolute transform -translate-x-1/2 -translate-y-full transition-all duration-200 z-10 ${isActive ? 'z-20' : ''}`}
            style={{ left: `${Math.max(5, Math.min(92, x))}%`, top: `${Math.max(8, Math.min(88, y))}%` }}
            aria-label={`${listing.title} - ${listing.price}`}>
            <div className={`map-price-marker ${isActive ? 'map-price-marker-active' : ''}`}>
              {listing.price}
            </div>
            <div className={`w-2 h-2 rounded-full mx-auto -mt-0.5 ${isActive ? 'bg-[#D93025]' : 'bg-[#1D1D1F]'}`} />
          </button>
        );
      })}
      <div className="absolute top-4 right-4 flex flex-col gap-2">
        <button className="w-9 h-9 bg-white rounded-[10px] shadow-card flex items-center justify-center text-[#1D1D1F] hover:bg-[#F5F5F7] font-700 text-lg transition-colors">+</button>
        <button className="w-9 h-9 bg-white rounded-[10px] shadow-card flex items-center justify-center text-[#1D1D1F] hover:bg-[#F5F5F7] font-700 text-lg transition-colors">−</button>
      </div>
      <div className="absolute top-4 left-1/2 -translate-x-1/2">
        <button className="flex items-center gap-2 bg-white text-[#1D1D1F] px-4 py-2 rounded-full shadow-card text-[12px] font-600 hover:bg-[#F5F5F7] transition-colors border border-[#E2E2E7]">
          <RefreshCw size={11} /> Search this area
        </button>
      </div>
      <div className="absolute bottom-5 right-4">
        <button className="w-10 h-10 bg-white rounded-full shadow-card flex items-center justify-center text-[#D93025] hover:bg-[#FFEBEE] transition-colors">
          <Navigation size={15} />
        </button>
      </div>
      <div className="absolute bottom-2 right-2 text-[9px] text-[#A1A1A6]">Map data © NammaPrice</div>
    </div>
  );
}

function ListingCard({ listing, isActive, onHover }: { listing: Listing; isActive: boolean; onHover: (id: string | null) => void }) {
  const [isFav, setIsFav] = useState(false);
  return (
    <div
      id={`listing-card-${listing.id}`}
      className={`property-card group cursor-pointer transition-all ${isActive ? 'ring-2 ring-[#D93025] shadow-card-hover' : ''}`}
      onMouseEnter={() => onHover(listing.id)}
      onMouseLeave={() => onHover(null)}>
      <div className="flex flex-col sm:flex-row">
        <div className="relative img-zoom-wrap flex-shrink-0" style={{ width: '100%', maxWidth: '220px', minHeight: '160px' }}>
          <AppImage src={listing.image} alt={listing.imageAlt} fill className="object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.35) 100%)' }} />
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {listing.isFeatured && <span className="badge-featured">Featured</span>}
          </div>
          <div className="absolute top-3 right-3">
            <button
              onClick={(e) => { e.preventDefault(); setIsFav(!isFav); }}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-sm touch-manipulation ${isFav ? 'bg-[#D93025] text-white' : 'bg-white/95 text-[#6E6E73] hover:text-[#D93025]'}`}
              aria-label="Save to favorites">
              <Heart size={13} fill={isFav ? 'currentColor' : 'none'} />
            </button>
          </div>
          <div className="absolute bottom-3 left-3">
            <span className={listing.status === 'For Sale' ? 'badge-for-sale' : 'badge-for-rent'}>
              {listing.status}
            </span>
          </div>
        </div>
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] font-600 text-[#6E6E73] bg-[#F5F5F7] px-2 py-0.5 rounded-full">{listing.type}</span>
              {listing.badge && listing.badge !== 'Featured' &&
                <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full ${listing.badge === 'Hot Offer' ? 'bg-[#FFEBEE] text-[#D93025]' : listing.badge === 'New' ? 'bg-blue-50 text-blue-600' : 'bg-[#F5F5F7] text-[#6E6E73]'}`}>{listing.badge}</span>
              }
            </div>
            <h3 className="font-700 text-[#1D1D1F] mb-1 leading-snug" style={{ fontSize: '14px', letterSpacing: '-0.01em' }}>
              <Link href={`/listing-detail-page?id=${listing.id}`} className="hover:text-[#D93025] transition-colors">
                {listing.title}
              </Link>
            </h3>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="font-700 text-[#D93025] price-tag" style={{ fontSize: '17px' }}>{listing.price}</span>
              {listing.pricePerSqft && <span className="text-[11px] text-[#A1A1A6]">{listing.pricePerSqft}</span>}
            </div>
            <div className="flex items-center gap-1 mb-2">
              <MapPin size={11} className="text-[#D93025] flex-shrink-0" />
              <span className="text-[12px] text-[#6E6E73] truncate">{listing.location}</span>
            </div>
            <div className="flex items-center gap-3">
              {listing.beds > 0 && <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]"><BedDouble size={12} className="text-[#D93025]" />{listing.beds}</span>}
              {listing.baths > 0 && <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]"><Bath size={12} className="text-[#D93025]" />{listing.baths}</span>}
              {listing.area && <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]"><Maximize2 size={12} className="text-[#D93025]" />{listing.area}</span>}
            </div>
          </div>
          <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-[#F0F0F5]">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#FFEBEE] flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-700 text-[#D93025]">{listing.seller?.charAt(0) || 'O'}</span>
              </div>
              <span className="text-[11px] text-[#6E6E73] truncate max-w-[80px]">{listing.seller}</span>
              {listing.isVerified && <span className="flex items-center gap-0.5 text-[10px] font-600 text-[#16A34A] flex-shrink-0"><CheckCircle size={10} fill="currentColor" />Verified</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <a href={`https://wa.me/91`} className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all touch-manipulation" aria-label="WhatsApp">
                <MessageCircle size={13} />
              </a>
              <a href={`tel:+91`} className="w-8 h-8 rounded-full bg-[#D93025]/10 flex items-center justify-center text-[#D93025] hover:bg-[#D93025] hover:text-white transition-all touch-manipulation" aria-label="Call">
                <Phone size={13} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonListingCard() {
  return (
    <div className="property-card animate-pulse">
      <div className="flex flex-col sm:flex-row">
        <div className="bg-[#E2E2E7] flex-shrink-0" style={{ width: '220px', minHeight: '170px' }} />
        <div className="flex-1 p-4 space-y-3">
          <div className="h-3 bg-[#E2E2E7] rounded-full w-1/4" />
          <div className="h-4 bg-[#E2E2E7] rounded-full w-3/4" />
          <div className="h-5 bg-[#E2E2E7] rounded-full w-1/3" />
          <div className="h-3 bg-[#E2E2E7] rounded-full w-1/2" />
        </div>
      </div>
    </div>
  );
}

// ─── Advanced Faceted Filter Panel ───────────────────────────────────────────
function FacetedFilterPanel({
  open,
  onClose,
  selectedAmenities,
  setSelectedAmenities,
  approvalStatus,
  setApprovalStatus,
  minSellerRating,
  setMinSellerRating,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  selectedAmenities: string[];
  setSelectedAmenities: (a: string[]) => void;
  approvalStatus: string;
  setApprovalStatus: (s: string) => void;
  minSellerRating: number;
  setMinSellerRating: (r: number) => void;
  minPrice: string;
  setMinPrice: (p: string) => void;
  maxPrice: string;
  setMaxPrice: (p: string) => void;
  onApply: () => void;
}) {
  if (!open) return null;

  const toggleAmenity = (a: string) => {
    setSelectedAmenities(
      selectedAmenities.includes(a)
        ? selectedAmenities.filter(x => x !== a)
        : [...selectedAmenities, a]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-sm bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-poppins font-semibold text-gray-900">Advanced Filters</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-6">
          {/* Price Range */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Price Range</p>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min ₹"
                value={minPrice}
                onChange={e => setMinPrice(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E4191B]"
              />
              <input
                type="number"
                placeholder="Max ₹"
                value={maxPrice}
                onChange={e => setMaxPrice(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E4191B]"
              />
            </div>
          </div>

          {/* Amenities */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Amenities</p>
            <div className="flex flex-wrap gap-2">
              {AMENITIES_OPTIONS.map(a => (
                <button
                  key={a}
                  onClick={() => toggleAmenity(a)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedAmenities.includes(a)
                      ? 'bg-[#E4191B] text-white border-[#E4191B]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#E4191B] hover:text-[#E4191B]'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Approval Status */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Approval Status</p>
            <div className="flex flex-wrap gap-2">
              {APPROVAL_STATUS_OPTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => setApprovalStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    approvalStatus === s
                      ? 'bg-[#E4191B] text-white border-[#E4191B]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#E4191B] hover:text-[#E4191B]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Seller Rating */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Seller Rating</p>
            <div className="space-y-2">
              {SELLER_RATING_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setMinSellerRating(opt.value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    minSellerRating === opt.value
                      ? 'bg-[#FFEBEE] border-[#E4191B] text-[#E4191B]'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-[#E4191B]'
                  }`}
                >
                  {opt.value > 0 && (
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: opt.value }).map((_, i) => (
                        <Star key={i} size={11} className="text-amber-400" fill="currentColor" />
                      ))}
                    </div>
                  )}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onApply}
            className="w-full bg-[#E4191B] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#c41515] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ListingsContent() {
  const searchParams = useSearchParams();
  const catSlug = searchParams?.get('cat') || null;

  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [facetedFilterOpen, setFacetedFilterOpen] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Category & subcategory state
  const [activeCategoryRow, setActiveCategoryRow] = useState<CategoryRow | null>(null);
  const [subcategories, setSubcategories] = useState<CategoryRow[]>([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);

  // Faceted filter state
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [approvalStatus, setApprovalStatus] = useState('All');
  const [minSellerRating, setMinSellerRating] = useState(0);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    amenities: [] as string[],
    approvalStatus: 'All',
    minSellerRating: 0,
    minPrice: '',
    maxPrice: '',
  });

  const activeFilterCount = appliedFilters.amenities.length +
    (appliedFilters.approvalStatus !== 'All' ? 1 : 0) +
    (appliedFilters.minSellerRating > 0 ? 1 : 0) +
    (appliedFilters.minPrice ? 1 : 0) +
    (appliedFilters.maxPrice ? 1 : 0);

  // Load category + subcategories when catSlug changes
  useEffect(() => {
    if (!catSlug) {
      setActiveCategoryRow(null);
      setSubcategories([]);
      setSelectedSubcategoryId(null);
      return;
    }
    setSubcategoriesLoading(true);
    getCategoryBySlug(catSlug).then(async (cat) => {
      setActiveCategoryRow(cat);
      setSelectedSubcategoryId(null);
      if (cat) {
        const subs = await getSubcategories(cat.id);
        setSubcategories(subs);
      } else {
        setSubcategories([]);
      }
      setSubcategoriesLoading(false);
    });
  }, [catSlug]);

  // Load listings
  useEffect(() => {
    setLoading(true);
    getActiveListings({
      search: keyword || undefined,
      sortBy,
      categoryId: activeCategoryRow && !selectedSubcategoryId ? activeCategoryRow.id : undefined,
      subcategoryId: selectedSubcategoryId || undefined,
      minPrice: appliedFilters.minPrice ? Number(appliedFilters.minPrice) : undefined,
      maxPrice: appliedFilters.maxPrice ? Number(appliedFilters.maxPrice) : undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }).then(({ listings: rows, count }) => {
      setListings(rows.map(mapToListingCard));
      setTotalCount(count);
      setLoading(false);
    });
  }, [keyword, sortBy, page, appliedFilters, activeCategoryRow, selectedSubcategoryId]);

  const handleApplyFilters = () => {
    setAppliedFilters({ selectedAmenities, approvalStatus, minSellerRating, minPrice, maxPrice } as any);
    setFacetedFilterOpen(false);
    setPage(1);
  };

  const handleMarkerClick = useCallback((id: string) => {
    setActiveListingId(id);
    const el = document.getElementById(`listing-card-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="flex flex-col h-full">
      {/* Sticky search + filter bar */}
      <div className="bg-white border-b border-[#E2E2E7] px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1A6]" />
            <input
              type="text"
              value={keyword}
              onChange={e => { setKeyword(e.target.value); setPage(1); }}
              placeholder="Search listings..."
              className="w-full pl-9 pr-3 py-2 border border-[#E2E2E7] rounded-[10px] text-[13px] focus:outline-none focus:border-[#D93025] bg-[#F5F5F7]"
            />
          </div>

          {/* Advanced filters button */}
          <button
            onClick={() => setFacetedFilterOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12px] font-600 border transition-colors relative ${
              activeFilterCount > 0 ? 'bg-[#FFEBEE] border-[#D93025] text-[#D93025]' : 'bg-white border-[#E2E2E7] text-[#6E6E73] hover:border-[#D93025]'
            }`}
          >
            <SlidersHorizontal size={13} />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#D93025] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Sort */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E2E2E7] rounded-[8px] text-[12px] font-600 text-[#6E6E73] hover:border-[#D93025] transition-colors"
            >
              {sortOptions.find(s => s.id === sortBy)?.label}
              <ChevronDown size={12} className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
            </button>
            {sortOpen && (
              <div className="absolute top-full right-0 mt-1.5 bg-white border border-[#E2E2E7] rounded-[12px] shadow-lg z-20 w-44 py-1.5">
                {sortOptions.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => { setSortBy(opt.id); setSortOpen(false); setPage(1); }}
                    className={`w-full text-left px-4 py-2 text-[12px] hover:bg-[#F5F5F7] transition-colors ${sortBy === opt.id ? 'text-[#D93025] font-600' : 'text-[#1D1D1F]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View toggle */}
          <div className="hidden md:flex items-center gap-1 bg-[#F5F5F7] rounded-[8px] p-0.5">
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-[6px] transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[#D93025]' : 'text-[#A1A1A6]'}`} aria-label="List view">
              <List size={14} />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-[6px] transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#D93025]' : 'text-[#A1A1A6]'}`} aria-label="Grid view">
              <Grid3X3 size={14} />
            </button>
          </div>

          {/* Mobile map/list toggle */}
          <div className="flex md:hidden items-center gap-1 bg-[#F5F5F7] rounded-[8px] p-0.5">
            <button onClick={() => setMobileView('list')} className={`p-1.5 rounded-[6px] transition-colors ${mobileView === 'list' ? 'bg-white shadow-sm text-[#D93025]' : 'text-[#A1A1A6]'}`} aria-label="List">
              <List size={14} />
            </button>
            <button onClick={() => setMobileView('map')} className={`p-1.5 rounded-[6px] transition-colors ${mobileView === 'map' ? 'bg-white shadow-sm text-[#D93025]' : 'text-[#A1A1A6]'}`} aria-label="Map">
              <Map size={14} />
            </button>
          </div>
        </div>

        {/* Subcategory filter row — shown when a category is active and has subcategories */}
        {(subcategories.length > 0 || subcategoriesLoading) && (
          <div className="flex items-center gap-2 mt-2.5 overflow-x-auto scrollbar-hide pb-0.5">
            {activeCategoryRow && (
              <button
                onClick={() => { setSelectedSubcategoryId(null); setPage(1); }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-600 whitespace-nowrap transition-all border ${
                  !selectedSubcategoryId
                    ? 'bg-[#D93025] text-white border-[#D93025]'
                    : 'bg-white text-[#6E6E73] border-[#E2E2E7] hover:border-[#D93025] hover:text-[#D93025]'
                }`}
              >
                All {activeCategoryRow.name}
              </button>
            )}
            {subcategoriesLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-shrink-0 h-7 w-20 bg-[#E2E2E7] rounded-full animate-pulse" />
                ))
              : subcategories.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => { setSelectedSubcategoryId(sub.id); setPage(1); }}
                    className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-600 whitespace-nowrap transition-all border ${
                      selectedSubcategoryId === sub.id
                        ? 'bg-[#D93025] text-white border-[#D93025]'
                        : 'bg-white text-[#6E6E73] border-[#E2E2E7] hover:border-[#D93025] hover:text-[#D93025]'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))
            }
          </div>
        )}

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {appliedFilters.amenities.map(a => (
              <span key={a} className="flex items-center gap-1 px-2 py-0.5 bg-[#FFEBEE] text-[#D93025] text-xs rounded-full">
                {a}
                <button onClick={() => {
                  const newAmenities = appliedFilters.amenities.filter(x => x !== a);
                  setSelectedAmenities(newAmenities);
                  setAppliedFilters(prev => ({ ...prev, amenities: newAmenities }));
                }}>
                  <X size={10} />
                </button>
              </span>
            ))}
            {appliedFilters.approvalStatus !== 'All' && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FFEBEE] text-[#D93025] text-xs rounded-full">
                Status: {appliedFilters.approvalStatus}
                <button onClick={() => { setApprovalStatus('All'); setAppliedFilters(prev => ({ ...prev, approvalStatus: 'All' })); }}>
                  <X size={10} />
                </button>
              </span>
            )}
            {appliedFilters.minSellerRating > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FFEBEE] text-[#D93025] text-xs rounded-full">
                {appliedFilters.minSellerRating}★+
                <button onClick={() => { setMinSellerRating(0); setAppliedFilters(prev => ({ ...prev, minSellerRating: 0 })); }}>
                  <X size={10} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* List panel */}
        <div className={`flex flex-col overflow-y-auto ${mobileView === 'map' ? 'hidden md:flex' : 'flex'} md:w-1/2 lg:w-3/5 xl:w-2/3`}>
          <div className="px-4 py-3 flex items-center justify-between bg-white border-b border-[#F0F0F5]">
            <p className="text-[13px] text-[#6E6E73] font-500">
              {loading ? 'Loading...' : `${totalCount.toLocaleString('en-IN')} ${activeCategoryRow ? activeCategoryRow.name : 'properties'} found`}
            </p>
          </div>

          <div className={`p-4 ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'space-y-3'}`}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonListingCard key={i} />)
              : listings.length > 0
              ? listings.map(l => (
                  <ListingCard key={l.id} listing={l} isActive={activeListingId === l.id} onHover={setActiveListingId} />
                ))
              : (
                <div className="text-center py-16 col-span-2">
                  <p className="text-[#A1A1A6] text-[14px]">No listings found matching your filters</p>
                  <button onClick={() => { setKeyword(''); setSelectedSubcategoryId(null); setAppliedFilters({ amenities: [], approvalStatus: 'All', minSellerRating: 0, minPrice: '', maxPrice: '' }); }} className="mt-3 text-[#D93025] text-sm font-600 hover:underline">
                    Clear all filters
                  </button>
                </div>
              )
            }
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-6 px-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-[8px] text-[13px] font-600 border border-[#E2E2E7] text-[#6E6E73] disabled:opacity-40 hover:border-[#D93025] hover:text-[#D93025] transition-colors"
              >
                Previous
              </button>
              <span className="text-[13px] text-[#6E6E73]">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-[8px] text-[13px] font-600 border border-[#E2E2E7] text-[#6E6E73] disabled:opacity-40 hover:border-[#D93025] hover:text-[#D93025] transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Map panel */}
        <div className={`flex-1 ${mobileView === 'list' ? 'hidden md:block' : 'block'}`}>
          <GoogleMapView listings={listings} activeId={activeListingId} onMarkerClick={handleMarkerClick} />
        </div>
      </div>

      {/* Faceted filter panel */}
      <FacetedFilterPanel
        open={facetedFilterOpen}
        onClose={() => setFacetedFilterOpen(false)}
        selectedAmenities={selectedAmenities}
        setSelectedAmenities={setSelectedAmenities}
        approvalStatus={approvalStatus}
        setApprovalStatus={setApprovalStatus}
        minSellerRating={minSellerRating}
        setMinSellerRating={setMinSellerRating}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        onApply={handleApplyFilters}
      />
    </div>
  );
}